import {
  Chips,
  ContentHeader,
  Css,
  DateFilterValue,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  RowStyles,
  SimpleHeaderAndData,
  Tag,
  checkboxFilter,
  column,
  dateColumn,
  dateFilter,
  multiFilter,
  numericColumn,
  pageContentPaddingX,
  simpleDataRows,
  useGridTableLayoutState,
} from "@homebound/beam";
import { sentenceCase } from "change-case";
import { useCallback, useMemo, useState } from "react";
import { createBillPageUrl, createBillUrl } from "src/RouteUrls";
import { dateCell, priceCell } from "src/components";
import { billStatusTagMapper } from "src/components/detailPane/bill/utils";
import { BillFilter, BillOrder, BillType, DateFilter2, InputMaybe, Order } from "src/generated/graphqlTypes";
import { FinancesPageSharedQuery } from "src/routes/billing/FinancesPage.generated";
import { BillsQuery, BillsQueryBillFragment, useBillsQuery } from "src/routes/bills/BillsPage.generated";
import { dateFilterOperations } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import { getProjectOptionMenuLabel } from "src/utils/projects";
import { financesDefaultFilter, financesDefaultPage } from "../billing/FinancesPage";
import { BillsCharts } from "./BillsCharts";

type BillsPageProps = { shared: FinancesPageSharedQuery };

export type BillsPageFilter = Omit<BillFilter, "dueDate" | "billDate"> & {
  dueDate: DateFilterValue<string>;
  billDate: DateFilterValue<string>;
};

export function BillsPage({ shared }: BillsPageProps) {
  const { markets, projects, internalUsers, tradePartners, currentInternalUser, enumDetails, customerOwnerEntities } =
    shared;
  const currentUserId = currentInternalUser?.id ?? "";
  const [order, setOrder] = useState<BillOrder>({ billDate: Order.Desc });
  const userOptions = useMemo(
    () => [{ id: currentUserId, name: "Me" }, ...internalUsers.filter(({ id }) => id !== currentUserId)],
    [currentUserId, internalUsers],
  );

  const filterDefs: FilterDefs<BillsPageFilter> = useMemo(
    () => ({
      marketId: multiFilter({
        label: "Market",
        options: markets,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      projectId: multiFilter({
        label: "Project",
        options: projects,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        getOptionMenuLabel: (o) => getProjectOptionMenuLabel(o),
      }),
      tradePartnerId: multiFilter({
        label: "Trade Partner",
        options: tradePartners,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      status: multiFilter({
        label: "Status",
        options: enumDetails.billStatus.map((status) => ({ value: status.code, name: status.name })),
        getOptionValue: (o) => o.value,
        getOptionLabel: (o) => o.name,
      }),
      pendingReview: checkboxFilter({ label: "Pending Review" }),
      reviewReason: multiFilter({
        label: "Review Reason",
        options: enumDetails.billReviewReason.map((status) => ({ value: status.code, name: status.name })),
        getOptionValue: (o) => o.value,
        getOptionLabel: (o) => o.name,
      }),
      type: multiFilter({
        label: "Type",
        options: Object.values(BillType).map((type) => ({ value: type, name: sentenceCase(type) })),
        getOptionValue: (o) => o.value,
        getOptionLabel: (o) => o.name,
      }),
      source: multiFilter({
        label: "Source",
        options: enumDetails.billSource.map((status) => ({ value: status.code, name: status.name })),
        getOptionValue: (o) => o.value,
        getOptionLabel: (o) => o.name,
      }),
      isDeferred: checkboxFilter({ label: "Deferred" }),
      customerOwnerEntityId: multiFilter({
        label: "Project Owner",
        options: customerOwnerEntities,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      createdBy: multiFilter({
        label: "Created By",
        options: internalUsers,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      approver: multiFilter({
        label: "Approver",
        options: userOptions,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      reviewer: multiFilter({
        label: "Reviewer",
        options: internalUsers,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      dueDate: dateFilter({
        label: "Due",
        operations: dateFilterOperations,
        getOperationLabel: (o) => o.label,
        getOperationValue: (o) => o.value,
        defaultValue: financesDefaultFilter.dueDate,
      }),
      billDate: dateFilter({
        label: "Bill Date",
        operations: dateFilterOperations,
        getOperationLabel: (o) => o.label,
        getOperationValue: (o) => o.value,
      }),
      hasBalanceDue: checkboxFilter({ label: "Balance Due" }),
    }),
    [customerOwnerEntities, enumDetails, internalUsers, markets, projects, tradePartners, userOptions],
  );
  const layoutState = useGridTableLayoutState<BillsPageFilter>({
    persistedFilter: { filterDefs, storageKey: "financesBillFilter" },
    search: "server",
  });
  const search = layoutState.searchString ?? "";
  const query = useBillsQuery({
    variables: { filter: { ...mapToFilter(layoutState.filter), search }, order, page: financesDefaultPage },
  });
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.billsPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.billsPage.entities.length, limit: financesDefaultPage.limit } },
      });
    }
  }, [query]);
  const columns = useMemo(() => getColumns(), []);
  const rowStyles = useMemo(() => createRowStyles(), []);
  const createRows = useCallback((data: BillsQuery | undefined) => simpleDataRows(data?.billsPage.entities ?? []), []);

  return (
    <>
      <div css={{ ...Css.layoutContainer.pt4.$, ...pageContentPaddingX }}>
        <BillsCharts filter={layoutState.filter} search={search} />
      </div>
      <ContentHeader
        title="Bills"
        actions={[{ label: "Create Bill", icon: "plus", variant: "secondary", onClick: createBillPageUrl() }]}
        xss={pageContentPaddingX}
      />
      <GridTableLayout
        layoutState={layoutState}
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          sorting: {
            on: "server",
            onSort: (key, direction) => setOrder(toOrder(key, direction)),
            value: parseOrder(order),
          },
          as: "virtual",
          style: { rowHeight: "flexible" },
          emptyFallback: "No bills found that matched given filters.",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </>
  );
}

type Row = SimpleHeaderAndData<BillsQueryBillFragment>;

function createRowStyles(): RowStyles<Row> {
  return { header: {}, data: { rowLink: ({ data }) => createBillUrl(data.id) } };
}

function getColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "project",
      name: "Project",
      header: "Project",
      data: ({ projectStage }) => projectStage.project.projectName,
      serverSideSortKey: "project",
      w: 2,
      mw: "220px",
      canHide: false,
    }),
    column<Row>({
      id: "tradePartner",
      name: "Trade Partner",
      header: "Trade Partner",
      data: ({ tradePartner }) => tradePartner.name,
      serverSideSortKey: "tradePartner",
      mw: "140px",
    }),
    column<Row>({
      id: "billNumber",
      name: "Bill Number",
      header: "Bill Number",
      data: ({ tradePartnerNumber, isTradePartnerCredit }) =>
        isTradePartnerCredit ? `Credit #${tradePartnerNumber}` : `Bill #${tradePartnerNumber}`,
      mw: "120px",
    }),
    column<Row>({ id: "billType", name: "Bill Type", header: "Bill Type", data: ({ type }) => type.name, mw: "100px" }),
    column<Row>({
      id: "billSource",
      name: "Bill Source",
      header: "Bill Source",
      data: ({ source }) => source.name,
      mw: "110px",
    }),
    column<Row>({
      id: "commitments",
      name: "Commitments",
      header: "Commitments",
      data: ({ parents }) => <Chips values={parents.map((p) => `#${p.accountingNumber}`)} />,
      serverSideSortKey: "commitment",
      w: 1.5,
      mw: "140px",
    }),
    dateColumn<Row>({
      id: "billDate",
      name: "Bill Date",
      header: "Bill Date",
      data: ({ billDate }) => dateCell(billDate),
      serverSideSortKey: "billDate",
      mw: "110px",
    }),
    dateColumn<Row>({
      id: "dueDate",
      name: "Due Date",
      header: "Due Date",
      data: ({ dueDate }) => dateCell(dueDate),
      serverSideSortKey: "dueDate",
      mw: "110px",
    }),
    numericColumn<Row>({
      id: "billAmount",
      name: "Bill Amount",
      header: "Bill Amount",
      data: ({ billedInCents, isTradePartnerCredit, pendingReview, proposedAmountInCents }) =>
        priceCell({
          valueInCents: pendingReview ? proposedAmountInCents : isTradePartnerCredit ? -billedInCents : billedInCents,
        }),
      serverSideSortKey: "billedInCents",
      mw: "120px",
    }),
    numericColumn<Row>({
      id: "paidToDate",
      name: "Paid to Date",
      header: "Paid to Date",
      data: ({ paidInCents, isTradePartnerCredit }) =>
        priceCell({ valueInCents: isTradePartnerCredit ? -paidInCents : paidInCents }),
      serverSideSortKey: "paidInCents",
      mw: "130px",
    }),
    numericColumn<Row>({
      id: "balanceDue",
      name: "Balance Due",
      header: "Balance Due",
      data: ({ balanceInCents, isTradePartnerCredit, pendingReview, proposedAmountInCents }) =>
        priceCell({
          valueInCents: pendingReview ? proposedAmountInCents : isTradePartnerCredit ? -balanceInCents : balanceInCents,
        }),
      serverSideSortKey: "balanceInCents",
      mw: "120px",
    }),
    dateColumn<Row>({
      id: "paidDate",
      name: "Paid Date",
      header: "Paid Date",
      data: ({ paidDate }) => dateCell(paidDate),
      serverSideSortKey: "paidDate",
      mw: "110px",
    }),
    column<Row>({
      id: "status",
      name: "Status",
      header: "Status",
      data: (row) => {
        const [billType, text] = billStatusTagMapper(row);
        return <Tag type={billType} text={text} />;
      },
      mw: "120px",
    }),
  ];
}

export function mapToFilter(filter: BillsPageFilter): BillFilter {
  const { dueDate, billDate, ...others } = filter;
  return {
    ...others,
    ...(dueDate ? { dueDate: { op: dueDate.op, value: dueDate.value } as InputMaybe<DateFilter2> } : {}),
    ...(billDate ? { billDate: { op: billDate.op, value: billDate.value } as InputMaybe<DateFilter2> } : {}),
  };
}
